import { useIonToast } from "@ionic/react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "components/Molecules/Button/Button";
import { checkmarkCircle } from "ionicons/icons";
import { DateTime } from "luxon";
import { useAuth } from "pages";

import { api, translate } from "~utils";

import { getSchoolBillingDates } from "./utils";

export function BillingPage() {
  /***** Hooks *****/
  const [showToast] = useIonToast();
  const { userInfo } = useAuth();

  /***** Mutation *****/
  const { isPending, mutate } = useMutation({
    mutationKey: ["exportBill"],
    mutationFn: async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      return api({
        method: "POST",
        url: "/api/admin/billing/export",
        data: { startDate, endDate },
      });
    },
    onSuccess() {
      showToast({
        position: "top",
        icon: checkmarkCircle,
        message: translate("BILLING_PAGE_TOAST_MESSAGE", { email: userInfo.email }),
        duration: 6000,
      });
    },
  });

  /***** Constants *****/
  const billingDateYears = getSchoolBillingDates();

  /***** Render *****/
  return (
    <BillingPageBase
      bills={billingDateYears}
      onClick={(params) => mutate(params)}
      isBillExportPending={isPending}
    />
  );
}

type BillingPageBaseProps = {
  /** An array of exportable bill ranges */
  bills: Array<{
    startDate: DateTime;
    endDate: DateTime;
  }>;
  /** Handler when clicking on the "Export" button beside each bill */
  onClick: (params: { startDate: string; endDate: string }) => void;
  /** Disables the "Export" button when true */
  isBillExportPending: boolean;
};

export function BillingPageBase(props: BillingPageBaseProps) {
  const { bills, onClick, isBillExportPending } = props;

  return (
    <div className="page">
      {/* Container */}
      <div css={{ marginTop: 8, background: "#FFF", borderRadius: 8, padding: 16 }}>
        {/* List */}
        <ul css={{ margin: 0, listStyle: "none", padding: 0 }}>
          {bills.map(({ startDate, endDate }, index) => (
            <BillListItem
              key={startDate.toISODate()}
              // Change the title based on if it's the first bill or not.
              title={translate(
                index === 0 ? "BILLING_PAGE_CURRENT_YEAR" : "BILLING_PAGE_PREVIOUS_YEAR",
                { startDate, endDate },
              )}
              subTitle={translate("BILLING_PAGE_DATE_RANGE", { startDate, endDate })}
              onClick={() =>
                onClick({ startDate: startDate.toISODate(), endDate: endDate.toISODate() })
              }
              isBillExportPending={isBillExportPending}
            />
          ))}
        </ul>

        {/* Contact Details */}
        <p
          dangerouslySetInnerHTML={{ __html: translate("BILLING_PAGE_CONTACT_MESSAGE") }}
          css={{ marginBottom: 0, marginTop: 16 }}
        />
      </div>
    </div>
  );
}

/********** Helper Components **********/
/**
 * Simple list item component which renders the title and subtitle to the left
 * and have a button to the right.
 */
type BillListItemProps = {
  title: string;
  subTitle: string;
  onClick: () => void;
  isBillExportPending: boolean;
};
function BillListItem(props: BillListItemProps) {
  const { title, subTitle, onClick, isBillExportPending } = props;

  /*** Render ***/
  return (
    <li
      css={{
        paddingTop: 16,
        paddingBottom: 16,
        borderBottom: "1px solid #E5E5E5",
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <div>
        <p css={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</p>
        <p css={{ margin: 0, fontSize: 16, fontWeight: 400 }}>{subTitle}</p>
      </div>
      <Button type="TERTIARY" label="Export" onClick={onClick} isDisabled={isBillExportPending} />
    </li>
  );
}
